// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = {
    title?: string;
    icon?: any;
    viewAllLink?: string;
    extendedStyles?: WidgetTitleStyles;
};

export interface WidgetTitleStyles {
    wrapper?: InjectableCss;
    iconWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    iconElement?: IconPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const widgetTitleStyles: WidgetTitleStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            padding-top: 25px;
            flex-wrap: wrap;
            justify-content: center;
            &&.with-icon {
                padding-left: 70px;
                position: relative;
            }
            @media print {
                display: none;
            }
        `,
    },
    iconWrapper: {
        css: css`
            line-height: 1;
            width: 55px;
            height: 61px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            left: 0;
            border-radius: 0 0 15px 15px;
            top: 0px;
            && > span,
            && svg {
                height: 100%;
                width: 100%;
            }
        `,
    },
    iconElement: {
        color: "common.white",
    },
    titleWrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: baseline;
            justify-content: center;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            text-align: center;
        `,
    },
    link: {
        css: css`
            margin-left: 6px;
        `,
    },
};

const WidgetTitle = ({ title, icon, viewAllLink, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(widgetTitleStyles, extendedStyles));

    return (
        <StyledWrapper
            {...styles.wrapper}
            className={`wrapper-element ${icon ? "with-icon" : ""}`}
            data-test-selector="WidgetTitle"
        >
            <StyledWrapper {...styles.iconWrapper}>{icon && <Icon src={icon} {...styles.iconElement} />}</StyledWrapper>
            <StyledWrapper {...styles.titleWrapper} className="title-wrapper">
                <Typography {...styles.title}>{title}</Typography>
                {viewAllLink && (
                    <Link href={viewAllLink} {...styles.link}>
                        <Icon src="ChevronRight" color="tertiary" />
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default WidgetTitle;
